import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { PhoneOff, Mic, MicOff, Video as VideoIcon, VideoOff } from "lucide-react";
import { WS_BASE } from "@/lib/api";

const ICE = { iceServers: [{ urls: "stun:stun.l.google.com:19302" }] };

export default function VideoCall() {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const localRef = useRef(null);
  const remoteRef = useRef(null);
  const pcRef = useRef(null);
  const wsRef = useRef(null);
  const localStream = useRef(null);
  const myId = useRef(Math.random().toString(36).slice(2));
  const [status, setStatus] = useState("Connecting…");
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);

  useEffect(() => {
    let mounted = true;

    const start = async () => {
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      } catch (e) {
        setStatus("Camera/mic permission denied");
        return;
      }
      if (!mounted) { stream.getTracks().forEach((t) => t.stop()); return; }
      localStream.current = stream;
      if (localRef.current) localRef.current.srcObject = stream;

      const ws = new WebSocket(`${WS_BASE}/signal/${roomId}?token=${localStorage.getItem("pe_token")}`);
      wsRef.current = ws;

      const createPc = () => {
        const pc = new RTCPeerConnection(ICE);
        stream.getTracks().forEach((t) => pc.addTrack(t, stream));
        pc.onicecandidate = (e) => {
          if (e.candidate) ws.send(JSON.stringify({ type: "candidate", candidate: e.candidate, from: myId.current }));
        };
        pc.ontrack = (e) => {
          if (remoteRef.current) remoteRef.current.srcObject = e.streams[0];
          setStatus("Connected");
        };
        pc.onconnectionstatechange = () => {
          if (["disconnected", "failed"].includes(pc.connectionState)) setStatus("Call ended");
        };
        pcRef.current = pc;
        return pc;
      };

      ws.onopen = () => {
        setStatus("Waiting for your match…");
        ws.send(JSON.stringify({ type: "hello", from: myId.current }));
      };

      ws.onmessage = async (evt) => {
        const msg = JSON.parse(evt.data);
        if (msg.from === myId.current) return;

        if (msg.type === "hello") {
          // initiator is the peer with the larger id
          if (myId.current > msg.from) {
            const pc = pcRef.current || createPc();
            const offer = await pc.createOffer();
            await pc.setLocalDescription(offer);
            ws.send(JSON.stringify({ type: "offer", sdp: offer, from: myId.current }));
          }
        } else if (msg.type === "offer") {
          const pc = pcRef.current || createPc();
          await pc.setRemoteDescription(new RTCSessionDescription(msg.sdp));
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          ws.send(JSON.stringify({ type: "answer", sdp: answer, from: myId.current }));
        } else if (msg.type === "answer") {
          await pcRef.current?.setRemoteDescription(new RTCSessionDescription(msg.sdp));
        } else if (msg.type === "candidate") {
          try { await pcRef.current?.addIceCandidate(new RTCIceCandidate(msg.candidate)); } catch (e) {}
        }
      };
    };

    start();
    return () => {
      mounted = false;
      wsRef.current?.close();
      pcRef.current?.close();
      localStream.current?.getTracks().forEach((t) => t.stop());
    };
  }, [roomId]);

  const toggleMic = () => {
    const track = localStream.current?.getAudioTracks()[0];
    if (track) { track.enabled = !track.enabled; setMicOn(track.enabled); }
  };
  const toggleCam = () => {
    const track = localStream.current?.getVideoTracks()[0];
    if (track) { track.enabled = !track.enabled; setCamOn(track.enabled); }
  };
  const hangup = () => { wsRef.current?.close(); pcRef.current?.close(); localStream.current?.getTracks().forEach((t) => t.stop()); navigate(-1); };

  return (
    <div className="fixed inset-0 z-50 bg-black flex items-center justify-center">
      <video ref={remoteRef} data-testid="remote-video" autoPlay playsInline className="w-full h-full object-cover" />
      <div className="absolute top-6 left-1/2 -translate-x-1/2 overline bg-black/50 px-4 py-2 rounded-full">{status}</div>
      <video ref={localRef} data-testid="local-video" autoPlay playsInline muted
        className="absolute bottom-28 right-6 w-32 h-44 md:w-48 md:h-64 object-cover rounded-2xl border border-[#D4AF37]/60" />

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-4 backdrop-blur-xl bg-black/60 px-6 py-4 rounded-full border border-white/10">
        <button data-testid="toggle-mic" onClick={toggleMic}
          className={`w-12 h-12 rounded-full flex items-center justify-center ${micOn ? "bg-white/10 text-white" : "bg-white text-black"}`}>
          {micOn ? <Mic size={20} /> : <MicOff size={20} />}
        </button>
        <button data-testid="hangup-btn" onClick={hangup}
          className="w-16 h-16 rounded-full bg-[#E63946] text-white flex items-center justify-center hover:brightness-110">
          <PhoneOff size={26} />
        </button>
        <button data-testid="toggle-cam" onClick={toggleCam}
          className={`w-12 h-12 rounded-full flex items-center justify-center ${camOn ? "bg-white/10 text-white" : "bg-white text-black"}`}>
          {camOn ? <VideoIcon size={20} /> : <VideoOff size={20} />}
        </button>
      </div>
    </div>
  );
}
